import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import SidePanel, { SidePanelSection, SidePanelActions, SidePanelDivider } from '@/components/SidePanel';
import { Library, Plus, Search, Tag, Package, Sparkles, ChevronRight, ChevronDown, Edit2, Trash2, Copy, Archive, RotateCcw, Upload, Settings, FileSpreadsheet } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
// iter-364 — Featured Brand Partner placement on Material Library.
import SponsorBadge from '@/components/SponsorBadge';

const UNIT_OPTIONS = ['No.s', 'Sq.ft', 'R.ft', 'L.ft', 'Kg', 'Ltr', 'Set', 'Pair', 'Box', 'Bag', 'Lot', 'Sheet', 'Bundle', 'Mtr', 'Sqm', 'Rmt', 'Cum'];

export default function MaterialsNew() {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedMaterials, setSelectedMaterials] = useState(new Set());
  
  // Side panel states
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [panelMode, setPanelMode] = useState('create'); // create, edit, view, bulk
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Bulk upload states
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkEntries, setBulkEntries] = useState([{ id: 1, name: '', brand: '', specifications: '', unit: 'No.s', rate: '' }]);
  const [bulkCategory, setBulkCategory] = useState('');
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  
  // Category management states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', description: '' });
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    brand: '',
    specifications: '',
    description: '',
    unit: 'No.s',
    quantity: '',
    rate: '',
  });
  
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchMaterials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/library/materials`);
      setMaterials(response.data.filter(m => m.status === 'active'));
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load materials');
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/library/materials/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [materialsRes, categoriesRes, projectsRes] = await Promise.all([
          axios.get(`${API}/library/materials`),
          axios.get(`${API}/library/materials/categories`),
          axios.get(`${API}/projects`)
        ]);
        setMaterials(materialsRes.data.filter(m => m.status === 'active'));
        setCategories(categoriesRes.data);
        setProjects(projectsRes.data);
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Group materials by category
  const groupedMaterials = materials
    .filter(m => 
      m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.brand?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .reduce((acc, material) => {
      const cat = material.category || 'Uncategorized';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(material);
      return acc;
    }, {});

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const openCreatePanel = () => {
    setFormData({
      name: '',
      category: '',
      brand: '',
      specifications: '',
      description: '',
      unit: 'No.s',
      quantity: '',
      rate: '',
    });
    setPanelMode('create');
    setPanelOpen(true);
    setPanelMinimized(false);
  };

  const openEditPanel = (material) => {
    setSelectedMaterial(material);
    setFormData({
      name: material.name,
      category: material.category || '',
      brand: material.brand || '',
      specifications: material.specifications || '',
      description: material.description || '',
      unit: material.unit || 'No.s',
      quantity: material.quantity || '',
      rate: material.rate || '',
    });
    setPanelMode('edit');
    setPanelOpen(true);
    setPanelMinimized(false);
  };

  const openViewPanel = (material) => {
    setSelectedMaterial(material);
    setPanelMode('view');
    setPanelOpen(true);
    setPanelMinimized(false);
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Material name is required');
      return;
    }

    try {
      await axios.post(`${API}/library/materials`, {
        ...formData,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        rate: formData.rate ? parseFloat(formData.rate) : null,
      });
      toast.success('Material added successfully');
      fetchMaterials();
      setPanelOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add material');
    }
  };

  const handleUpdate = async () => {
    if (!selectedMaterial) return;
    
    try {
      await axios.put(`${API}/library/materials/${selectedMaterial.id}`, {
        ...formData,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        rate: formData.rate ? parseFloat(formData.rate) : null,
      });
      toast.success('Material updated successfully');
      fetchMaterials();
      setPanelOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update material');
    }
  };

  const handleDelete = async () => {
    if (!selectedMaterial) return;
    
    try {
      await axios.delete(`${API}/library/materials/${selectedMaterial.id}/permanent`);
      toast.success('Material deleted');
      fetchMaterials();
      setDeleteDialogOpen(false);
      setPanelOpen(false);
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    try {
      await axios.post(`${API}/library/materials/categories`, { name: newCategoryName.trim() });
      toast.success('Category added');
      setFormData({ ...formData, category: newCategoryName.trim() });
      setNewCategoryName('');
      setShowCategoryInput(false);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  // =============== BULK UPLOAD FUNCTIONS ===============
  const openBulkDialog = () => {
    setBulkEntries([{ id: 1, name: '', brand: '', specifications: '', unit: 'No.s', rate: '' }]);
    setBulkCategory('');
    setCategorySearchQuery('');
    setBulkDialogOpen(true);
  };

  const addBulkEntry = () => {
    setBulkEntries(prev => [...prev, { 
      id: Date.now(), 
      name: '', 
      brand: '', 
      specifications: '', 
      unit: 'No.s', 
      rate: '' 
    }]);
  };

  const removeBulkEntry = (id) => {
    if (bulkEntries.length > 1) {
      setBulkEntries(prev => prev.filter(entry => entry.id !== id));
    }
  };

  const updateBulkEntry = (id, field, value) => {
    setBulkEntries(prev => prev.map(entry => 
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  const handleBulkSubmit = async () => {
    if (!bulkCategory) {
      toast.error('Please select a category');
      return;
    }

    const validEntries = bulkEntries.filter(e => e.name.trim());
    if (validEntries.length === 0) {
      toast.error('Please add at least one material with a name');
      return;
    }

    try {
      const materialsToCreate = validEntries.map(entry => ({
        name: entry.name.trim(),
        category: bulkCategory,
        brand: entry.brand || '',
        specifications: entry.specifications || '',
        unit: entry.unit || 'No.s',
        rate: entry.rate ? parseFloat(entry.rate) : null,
      }));

      await axios.post(`${API}/library/materials/bulk-import`, { materials: materialsToCreate });
      toast.success(`${validEntries.length} materials added successfully`);
      setBulkDialogOpen(false);
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add materials');
    }
  };

  const filteredCategories = categories.filter(cat => {
    const catName = typeof cat === 'string' ? cat : cat.name;
    return catName?.toLowerCase().includes(categorySearchQuery.toLowerCase());
  });

  const validBulkCount = bulkEntries.filter(e => e.name.trim()).length;

  // =============== CATEGORY MANAGEMENT FUNCTIONS ===============
  const openCategoryManager = () => {
    setCategoryDialogOpen(true);
    setEditingCategory(null);
    setCategoryFormData({ name: '', description: '' });
  };

  const startEditCategory = (category) => {
    const catName = typeof category === 'string' ? category : category.name;
    setEditingCategory(catName);
    setCategoryFormData({ 
      name: catName, 
      description: '' 
    });
  };

  const handleSaveCategory = async () => {
    if (!categoryFormData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        // Update existing category - editingCategory is now always a string
        await axios.put(`${API}/library/materials/categories/${encodeURIComponent(editingCategory)}`, {
          name: categoryFormData.name.trim(),
          description: categoryFormData.description
        });
        toast.success('Category updated');
      } else {
        // Create new category
        await axios.post(`${API}/library/materials/categories`, {
          name: categoryFormData.name.trim(),
          description: categoryFormData.description
        });
        toast.success('Category created');
      }
      setEditingCategory(null);
      setCategoryFormData({ name: '', description: '' });
      fetchCategories();
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const confirmDeleteCategory = (category) => {
    // category is now always a string
    setCategoryToDelete(category);
    setDeleteCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    // categoryToDelete is now always a string
    try {
      await axios.delete(`${API}/library/materials/categories/${encodeURIComponent(categoryToDelete)}`);
      toast.success('Category deleted');
      setDeleteCategoryDialogOpen(false);
      setCategoryToDelete(null);
      fetchCategories();
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const totalMaterials = materials.length;
  const totalCategories = Object.keys(groupedMaterials).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* iter-364 — Sponsored placement (zone=material_library). */}
        <SponsorBadge zone="material_library" limit={1} variant="premium-minimal" />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-stone-900">Material Library</h1>
            <p className="text-stone-500 mt-1">{totalMaterials} materials in {totalCategories} categories</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={openCategoryManager} variant="outline" data-testid="manage-categories-btn">
              <Settings size={18} className="mr-2" />
              Manage Categories
            </Button>
            <Button onClick={openBulkDialog} variant="outline" data-testid="bulk-upload-btn">
              <Upload size={18} className="mr-2" />
              Bulk Upload
            </Button>
            <Button onClick={openCreatePanel} className="apple-button" data-testid="add-material-btn">
              <Plus size={18} className="mr-2" />
              Add Material
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Search materials, categories, brands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white"
            data-testid="material-search"
          />
        </div>

        {/* Category Groups */}
        <div className="space-y-2">
          {Object.keys(groupedMaterials).length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
              <Library size={48} className="mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500">No materials found</p>
              <Button onClick={openCreatePanel} variant="outline" className="mt-4">
                <Plus size={16} className="mr-2" />
                Add your first material
              </Button>
            </div>
          ) : (
            Object.entries(groupedMaterials).map(([category, categoryMaterials]) => (
              <div key={category} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between p-4 hover:bg-stone-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {expandedCategories.has(category) ? (
                      <ChevronDown size={20} className="text-stone-400" />
                    ) : (
                      <ChevronRight size={20} className="text-stone-400" />
                    )}
                    <Tag size={18} className="text-blue-500" />
                    <span className="font-medium text-stone-900">{category}</span>
                    <span className="text-sm text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">
                      {categoryMaterials.length}
                    </span>
                  </div>
                </button>

                {/* Materials List */}
                {expandedCategories.has(category) && (
                  <div className="border-t border-stone-100">
                    {categoryMaterials.map((material) => (
                      <div
                        key={material.id}
                        onClick={() => openViewPanel(material)}
                        className="flex items-center justify-between px-4 py-3 hover:bg-stone-50 cursor-pointer border-b border-stone-50 last:border-0"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                            <Package size={18} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">{material.name}</p>
                            <p className="text-sm text-stone-500">
                              {material.brand && `${material.brand} • `}
                              {material.unit}
                              {material.rate && ` • ₹${material.rate.toLocaleString()}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditPanel(material); }}
                            className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
                          >
                            <Edit2 size={16} className="text-stone-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Side Panel */}
      <SidePanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        onMinimize={setPanelMinimized}
        minimized={panelMinimized}
        title={panelMode === 'create' ? 'Add Material' : panelMode === 'edit' ? 'Edit Material' : selectedMaterial?.name}
        subtitle={panelMode === 'view' ? selectedMaterial?.category : undefined}
      >
        {panelMode === 'view' && selectedMaterial ? (
          // View Mode
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                <Package size={32} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-stone-900">{selectedMaterial.name}</h3>
                <p className="text-stone-500">{selectedMaterial.category}</p>
              </div>
            </div>

            <SidePanelDivider />

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="text-xs text-stone-500 uppercase">Unit</p>
                <p className="font-medium text-stone-900">{selectedMaterial.unit}</p>
              </div>
              {selectedMaterial.rate && (
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 uppercase">Rate</p>
                  <p className="font-medium text-stone-900">₹{selectedMaterial.rate.toLocaleString()}</p>
                </div>
              )}
              {selectedMaterial.brand && (
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 uppercase">Brand</p>
                  <p className="font-medium text-stone-900">{selectedMaterial.brand}</p>
                </div>
              )}
              {selectedMaterial.quantity && (
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 uppercase">Quantity</p>
                  <p className="font-medium text-stone-900">{selectedMaterial.quantity}</p>
                </div>
              )}
            </div>

            {selectedMaterial.specifications && (
              <div>
                <p className="text-xs text-stone-500 uppercase mb-2">Specifications</p>
                <p className="text-stone-700 bg-stone-50 p-3 rounded-lg">{selectedMaterial.specifications}</p>
              </div>
            )}

            {selectedMaterial.description && (
              <div>
                <p className="text-xs text-stone-500 uppercase mb-2">Description</p>
                <p className="text-stone-700 bg-stone-50 p-3 rounded-lg">{selectedMaterial.description}</p>
              </div>
            )}

            <SidePanelActions>
              <Button onClick={() => openEditPanel(selectedMaterial)} className="flex-1">
                <Edit2 size={16} className="mr-2" />
                Edit
              </Button>
              <Button 
                variant="outline" 
                className="text-red-600 hover:bg-red-50"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </Button>
            </SidePanelActions>
          </div>
        ) : (
          // Create/Edit Mode
          <div className="space-y-4">
            <div>
              <Label className="text-stone-700">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Marine Plywood 18mm"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-stone-700">Category</Label>
              {showCategoryInput ? (
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="New category name"
                    className="flex-1"
                  />
                  <Button onClick={handleAddCategory} size="sm">Add</Button>
                  <Button variant="outline" size="sm" onClick={() => setShowCategoryInput(false)}>Cancel</Button>
                </div>
              ) : (
                <div className="flex gap-2 mt-1">
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => {
                        const catName = typeof cat === 'string' ? cat : cat.name;
                        return (
                          <SelectItem key={catName} value={catName}>{catName}</SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={() => setShowCategoryInput(true)}>
                    <Plus size={16} />
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-700">Unit</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((unit) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-stone-700">Rate (₹)</Label>
                <Input
                  type="number"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-stone-700">Brand</Label>
              <Input
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                placeholder="e.g., Century, Asian Paints"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-stone-700">Specifications</Label>
              <Textarea
                value={formData.specifications}
                onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                placeholder="Technical specifications..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label className="text-stone-700">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Additional details..."
                className="mt-1"
                rows={2}
              />
            </div>

            <SidePanelActions>
              <Button variant="outline" onClick={() => setPanelOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={panelMode === 'create' ? handleCreate : handleUpdate} 
                className="flex-1 apple-button"
              >
                {panelMode === 'create' ? 'Add Material' : 'Save Changes'}
              </Button>
            </SidePanelActions>
          </div>
        )}
      </SidePanel>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Material?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{selectedMaterial?.name}&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope text-2xl text-stone-900">Bulk Upload Materials</DialogTitle>
            <DialogDescription>Add multiple materials under the same category</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Category Selection */}
            <div>
              <Label className="text-stone-700">Category *</Label>
              <div className="mt-1 space-y-2">
                <Input
                  placeholder="Type to search categories..."
                  value={categorySearchQuery}
                  onChange={(e) => setCategorySearchQuery(e.target.value)}
                />
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {categorySearchQuery.trim() && !categories.some(c => {
                    const name = typeof c === 'string' ? c : c.name;
                    return name?.toLowerCase() === categorySearchQuery.trim().toLowerCase();
                  }) && (
                    <button
                      onClick={() => {
                        const newCat = categorySearchQuery.trim();
                        axios.post(`${API}/library/materials/categories`, { name: newCat })
                          .then(() => {
                            setBulkCategory(newCat);
                            fetchCategories();
                            toast.success('Category created');
                          })
                          .catch(() => toast.error('Failed to create category'));
                      }}
                      className="w-full p-3 text-left hover:bg-blue-50 text-blue-600 font-medium flex items-center gap-2 border-b"
                    >
                      <Plus size={16} />
                      Create New Category &quot;{categorySearchQuery}&quot;
                    </button>
                  )}
                  {filteredCategories.map((cat) => {
                    const catName = typeof cat === 'string' ? cat : cat.name;
                    return (
                      <button
                        key={catName}
                        onClick={() => setBulkCategory(catName)}
                        className={`w-full p-3 text-left hover:bg-stone-50 flex items-center justify-between ${
                          bulkCategory === catName ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                      >
                        <span>{catName}</span>
                        {bulkCategory === catName && <span className="text-blue-600">✓</span>}
                      </button>
                    );
                  })}
                </div>
                {bulkCategory && (
                  <p className="text-sm text-stone-600">Selected: <strong>{bulkCategory}</strong></p>
                )}
              </div>
            </div>

            {/* Material Entries */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-stone-700">Materials ({bulkEntries.length})</Label>
                <Button variant="outline" size="sm" onClick={addBulkEntry}>
                  <Plus size={14} className="mr-1" />
                  Add Row
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 gap-2 p-3 bg-stone-100 text-xs font-medium text-stone-600 uppercase">
                  <div className="col-span-3">Name *</div>
                  <div className="col-span-2">Brand</div>
                  <div className="col-span-3">Specifications</div>
                  <div className="col-span-1">Unit</div>
                  <div className="col-span-2">Rate (₹)</div>
                  <div className="col-span-1"></div>
                </div>
                
                <div className="divide-y max-h-64 overflow-y-auto">
                  {bulkEntries.map((entry, index) => (
                    <div key={entry.id} className="grid grid-cols-12 gap-2 p-2 items-center">
                      <div className="col-span-3">
                        <Input
                          placeholder="Material name..."
                          value={entry.name}
                          onChange={(e) => updateBulkEntry(entry.id, 'name', e.target.value)}
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          placeholder="Brand"
                          value={entry.brand}
                          onChange={(e) => updateBulkEntry(entry.id, 'brand', e.target.value)}
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          placeholder="Specifications"
                          value={entry.specifications}
                          onChange={(e) => updateBulkEntry(entry.id, 'specifications', e.target.value)}
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-1">
                        <Select value={entry.unit} onValueChange={(v) => updateBulkEntry(entry.id, 'unit', v)}>
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          placeholder="0"
                          value={entry.rate}
                          onChange={(e) => updateBulkEntry(entry.id, 'rate', e.target.value)}
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-1 flex justify-center">
                        {bulkEntries.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeBulkEntry(entry.id)}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="text-sm text-stone-500 mt-2">
                {validBulkCount} material{validBulkCount !== 1 ? 's' : ''} ready to create
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkSubmit} 
                className="apple-button"
                disabled={!bulkCategory || validBulkCount === 0}
              >
                <Upload size={16} className="mr-2" />
                Create {validBulkCount} Material{validBulkCount !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope text-2xl text-stone-900">Manage Categories</DialogTitle>
            <DialogDescription>Edit or delete material categories</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Add/Edit Category Form */}
            <div className="p-4 bg-stone-50 rounded-lg space-y-3">
              <Label className="text-stone-700">{editingCategory ? 'Edit Category' : 'Add New Category'}</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Category name"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  className="flex-1"
                />
                <Button onClick={handleSaveCategory} className="apple-button">
                  {editingCategory ? 'Update' : 'Add'}
                </Button>
                {editingCategory && (
                  <Button variant="outline" onClick={() => {
                    setEditingCategory(null);
                    setCategoryFormData({ name: '', description: '' });
                  }}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>

            {/* Category List */}
            <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
              {categories.length === 0 ? (
                <div className="p-8 text-center text-stone-500">
                  <Tag className="mx-auto mb-2" size={32} />
                  <p>No categories yet</p>
                </div>
              ) : (
                categories.map((cat) => {
                  const catName = typeof cat === 'string' ? cat : cat.name;
                  const materialCount = materials.filter(m => m.category === catName).length;
                  return (
                    <div key={catName} className="flex items-center justify-between p-4 hover:bg-stone-50">
                      <div className="flex items-center gap-3">
                        <Tag size={16} className="text-blue-500" />
                        <div>
                          <p className="font-medium text-stone-900">{catName}</p>
                          <p className="text-sm text-stone-500">{materialCount} material{materialCount !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => startEditCategory(catName)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => confirmDeleteCategory(catName)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the category &quot;{categoryToDelete}&quot;. 
              Materials in this category will become uncategorized.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-red-600 hover:bg-red-700">
              Delete Category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
